import type { ToolBadgeSpec } from '../types.js';

import { readArray, readField, readString, truncateForLabel } from '../extractors.js';

function readStringArray(value: unknown): string[] {
  const arr = readArray(value);
  if (!arr) return [];
  return arr.map((v) => readString(v)).filter((v): v is string => !!v);
}

/** Pull destination names from the input's destination_constraints taxonomy.
 *  Only uses named levels (cities, regions); country codes and internal IDs are skipped. */
function getInputDestinationNames(toolCall: { input?: Record<string, unknown> }): string[] {
  const constraints = readField(toolCall.input, 'destination_constraints');
  if (!constraints) return [];
  const cities = readStringArray(readField(constraints, 'city_names'));
  if (cities.length > 0) return cities;
  const regions = readStringArray(readField(constraints, 'regions'));
  return regions;
}

/** Aggregate unique destination names from the output hotel results. */
function getOutputDestinationNames(toolCall: { output?: unknown }): string[] {
  const arr = readArray(readField(toolCall.output, 'results'));
  if (!arr) return [];
  const seen = new Set<string>();
  const names: string[] = [];
  for (const entry of arr) {
    const name = readString(readField(readField(entry, 'destination'), 'name'));
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}

/** Summarize the search's destination scope into a "Paris, Rome" suffix. Prefers input
 *  constraints (what was requested) and falls back to destinations seen in the output. */
function getDestinationSummary(toolCall: { input?: Record<string, unknown>; output?: unknown }): string | undefined {
  const fromInput = getInputDestinationNames(toolCall);
  const names = fromInput.length > 0 ? fromInput : getOutputDestinationNames(toolCall);
  if (names.length === 0) return undefined;
  return truncateForLabel(names.join(', '));
}

export const fetchHotelDataSpec: ToolBadgeSpec = {
  toolName: 'fetchHotelData',
  aliases: ['searchHotels'],
  icon: 'hotel',
  label: (toolCall, active) => {
    if (readString(readField(toolCall.input, 'search_strategy')) === 'direct_lookup') {
      const query = readString(readField(toolCall.input, 'search_query'));
      const base = active ? 'Fetching info for' : 'Fetched info for';
      return query ? `${base} ${truncateForLabel(query)}` : base;
    }
    const base = active ? 'Searching for hotels' : 'Searched for hotels';
    const location = getDestinationSummary(toolCall);
    return location ? `${base} in ${location}` : base;
  },
  activitySummary: { label: (_, active) => (active ? 'Searching for hotels' : 'Searched for hotels') },
  resultNoun: { singular: 'hotel', plural: 'hotels' },
  extractResults: (toolCall) => {
    // Direct lookups and geo-precision searches return results that aren't necessarily
    // matches for what the user asked for, so the dropdown is only useful for vibe searches.
    if (readString(readField(toolCall.input, 'search_strategy')) !== 'vibe_hybrid') return undefined;
    const arr = readArray(readField(toolCall.output, 'results'));
    if (!arr) return undefined;
    const items = [];
    for (const [index, entry] of arr.entries()) {
      const id = readString(readField(entry, '_id')) ?? readString(readField(entry, 'id'));
      if (!id) continue;
      const title = readString(readField(entry, 'name')) ?? `Result ${index + 1}`;
      const subtitle = readString(readField(entry, 'formattedAddress')) ?? readString(readField(entry, 'address'));
      const brandLogo = readString(readField(readField(entry, 'brand'), 'logo'));
      items.push({ key: `${id}-${index}`, title, subtitle, previewType: 'hotel' as const, id, iconUrl: brandLogo });
    }
    const location = getDestinationSummary(toolCall);
    return { items, contextLabel: location ? `in ${location}` : undefined };
  },
};
